import type { Timestamp } from "firebase/firestore";

export type Difficulty = "story" | "investigation" | "expert";
export type TeamRole = "leader" | "member";

export interface AppUser {
  id: string;
  email: string;
  displayName: string;
  pseudonym: string;
  isAdmin: boolean;
  studioAccess?: boolean;
  createdAt?: Timestamp;
}

export type StudioRole =
  "producer" | "writer" | "developer" | "tester" | "publisher";
export type StudioPermission =
  | "edit_overview"
  | "manage_workflow"
  | "manage_team"
  | "edit_content"
  | "edit_technical"
  | "test_sandbox"
  | "report_issues"
  | "approve_release";
export type ProductionStageId =
  | "brief"
  | "narrative"
  | "route"
  | "puzzles"
  | "development"
  | "qa"
  | "sandbox"
  | "launch";

export interface StudioMember {
  uid: string;
  email: string;
  displayName: string;
  role: StudioRole;
  permissions: StudioPermission[];
}

export type StudioNotificationKind =
  | "assignment"
  | "due_date"
  | "comment"
  | "mention"
  | "validation"
  | "blocked"
  | "announcement"
  | "message";
export interface StudioNotification {
  id: string;
  projectId: string;
  projectTitle: string;
  kind: StudioNotificationKind;
  title: string;
  body: string;
  actorId: string;
  actorName: string;
  entityType?: "task" | "workspace" | "conversation";
  entityId?: string;
  read: boolean;
  createdAt?: Timestamp;
  readAt?: Timestamp;
}
export type StudioConversationType = "channel" | "announcement" | "direct";
export interface StudioConversation {
  id: string;
  title: string;
  type: StudioConversationType;
  participantIds: string[];
  visibleTo: string[];
  createdBy: string;
  createdByName: string;
  lastMessage?: string;
  lastMessageAt?: Timestamp;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}
export interface StudioMessageReaction {
  emoji: string;
  userIds: string[];
}
export interface StudioMessageAttachment {
  fileId: string;
  name: string;
  size: number;
  contentType: string;
  storagePath: string;
}
export interface StudioMessage {
  id: string;
  authorId: string;
  authorName: string;
  body: string;
  mentionedUserIds: string[];
  replyToId?: string;
  reactions: StudioMessageReaction[];
  attachments?: StudioMessageAttachment[];
  readBy?: string[];
  createdAt?: Timestamp;
}
export interface StudioPresence {
  uid: string;
  displayName: string;
  status: "online" | "away" | "offline";
  conversationId?: string;
  typing?: boolean;
  lastSeen?: Timestamp;
}
export interface StudioNotificationPreferences {
  inApp: boolean;
  email: boolean;
  mobile: boolean;
  mentions: boolean;
  assignments: boolean;
  dueDates: boolean;
  comments: boolean;
  validations: boolean;
  blocked: boolean;
  announcements: boolean;
  messages: boolean;
}

export type StudioAiActionType =
  | "create_task"
  | "create_workspace_document"
  | "update_blueprint"
  | "create_puzzle";
export interface StudioAiAction {
  id: string;
  type: StudioAiActionType;
  title: string;
  description: string;
  payload: Record<string, unknown>;
  status: "proposed" | "applied" | "rejected";
  appliedBy?: string;
  appliedByName?: string;
  appliedAt?: Timestamp;
}
export interface StudioAiConversation {
  id: string;
  title: string;
  createdBy: string;
  createdByName: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}
export interface StudioAiMessage {
  id: string;
  role: "user" | "assistant";
  body: string;
  authorId: string;
  authorName: string;
  actions?: StudioAiAction[];
  createdAt?: Timestamp;
}

export interface GameProject {
  id: string;
  title: string;
  city: string;
  description: string;
  estimatedMinutes: number;
  availableFrom?: string;
  availableUntil?: string;
  technicalNotes?: string;
  blueprint?: GameBlueprint;
  releaseChecks?: Record<string, boolean>;
  status: "draft" | "production" | "sandbox" | "ready" | "published";
  currentStage: ProductionStageId;
  completedStages: ProductionStageId[];
  memberIds: string[];
  members: StudioMember[];
  createdBy: string;
  sandboxEnabled: boolean;
  published?: boolean;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  storageBytes?: number;
  storageQuotaBytes?: number;
}

export interface GameBlueprint {
  elevatorPitch?: string;
  playerPromise?: string;
  targetAudience?: string;
  objectives?: string;
  themes?: string;
  tone?: string;
  synopsis?: string;
  opening?: string;
  ending?: string;
  characters?: string;
  loreRules?: string;
  routeStart?: string;
  routeEnd?: string;
  distanceKm?: string;
  routeDuration?: string;
  accessibility?: string;
  safetyPlan?: string;
  weatherFallback?: string;
  permits?: string;
  coreLoop?: string;
  progression?: string;
  difficultyStrategy?: string;
  hintStrategy?: string;
  failStates?: string;
  replayability?: string;
  visualDirection?: string;
  audioDirection?: string;
  uiNotes?: string;
  technicalArchitecture?: string;
  integrations?: string;
  analyticsEvents?: string;
  offlinePlan?: string;
  privacyNotes?: string;
  businessModel?: string;
  operatingPlan?: string;
  partnerships?: string;
  successMetrics?: string;
  launchPlan?: string;
  supportPlan?: string;
}

export type StudioTaskStatus =
  "backlog" | "todo" | "in_progress" | "review" | "testing" | "done";
export type StudioTaskPriority = "low" | "normal" | "high" | "critical";
export type StudioTaskType =
  "feature" | "story" | "task" | "puzzle" | "bug" | "improvement";
export interface StudioTaskComment {
  id: string;
  authorId: string;
  authorName: string;
  body: string;
  createdAt?: Timestamp;
}
export interface StudioTaskActivity {
  id: string;
  actorId: string;
  actorName: string;
  action: string;
  createdAt?: Timestamp;
}
export interface StudioTask {
  id: string;
  key?: string;
  title: string;
  description: string;
  acceptanceCriteria?: string;
  stageId: ProductionStageId;
  status: StudioTaskStatus;
  priority: StudioTaskPriority;
  type?: StudioTaskType;
  assigneeId?: string;
  dueDate?: string;
  sprintId?: string;
  parentTaskId?: string;
  blockedBy?: string[];
  labels?: string[];
  storyPoints?: number;
  comments?: StudioTaskComment[];
  activity?: StudioTaskActivity[];
  createdBy?: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

export type StudioSprintStatus = "planned" | "active" | "completed";
export interface StudioSprint {
  id: string;
  name: string;
  goal: string;
  startDate: string;
  endDate: string;
  status: StudioSprintStatus;
  createdBy?: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

export type WorkspaceItemType =
  "document" | "resource" | "discussion" | "whiteboard" | "decision" | "event";
export type WorkspaceItemStatus = "draft" | "review" | "approved" | "archived";
export interface WorkspaceComment {
  id: string;
  authorId: string;
  authorName: string;
  body: string;
  createdAt?: Timestamp;
}
export interface WorkspaceVersion {
  id: string;
  authorId: string;
  authorName: string;
  summary: string;
  createdAt?: Timestamp;
}
export interface WorkspaceItem {
  id: string;
  type: WorkspaceItemType;
  title: string;
  body: string;
  url?: string;
  status: WorkspaceItemStatus;
  assigneeId?: string;
  linkedTaskId?: string;
  eventDate?: string;
  labels?: string[];
  color?: string;
  createdBy: string;
  createdByName?: string;
  comments?: WorkspaceComment[];
  versions?: WorkspaceVersion[];
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

export interface WorkspaceFolder {
  id: string;
  name: string;
  parentId: string;
  archived: boolean;
  createdBy: string;
  createdByName?: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  archivedAt?: Timestamp;
}

export interface WorkspaceFileVersion {
  id: string;
  name: string;
  size: number;
  contentType: string;
  storagePath: string;
  authorId: string;
  authorName: string;
  createdAt?: Timestamp;
}

export interface WorkspaceFile {
  id: string;
  name: string;
  folderId: string;
  size: number;
  contentType: string;
  storagePath: string;
  latestVersionId: string;
  versionCount: number;
  versions: WorkspaceFileVersion[];
  archived: boolean;
  createdBy: string;
  createdByName?: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  archivedAt?: Timestamp;
}

export type StudioIssueSeverity = "minor" | "major" | "blocker";
export type StudioIssueStatus =
  "open" | "in_progress" | "resolved" | "verified";
export interface StudioIssue {
  id: string;
  title: string;
  steps: string;
  expected: string;
  actual: string;
  environment: string;
  severity: StudioIssueSeverity;
  status: StudioIssueStatus;
  puzzleId?: string;
  assigneeId?: string;
  reportedBy?: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

export interface DraftPuzzle {
  mode?: "shared" | "cooperative";
  fragments?: string[];
  minPlayers?: number;
  id: string;
  order: number;
  title: string;
  story: string;
  question: string;
  answer: string;
  hints: string[];
  latitude?: number | null;
  longitude?: number | null;
  radiusMeters?: number | null;
}

export interface Game {
  id: string;
  title: string;
  city: string;
  description: string;
  coverUrl?: string;
  published: boolean;
  estimatedMinutes: number;
  puzzleCount?: number;
  availableFrom?: string;
  availableUntil?: string;
}

export interface TeamMember {
  uid: string;
  email: string;
  pseudonym: string;
  role: TeamRole;
  canDecide: boolean;
  joinedAt?: Timestamp;
}

export interface Team {
  id: string;
  name: string;
  gameId: string;
  gameTitle: string;
  leaderId: string;
  difficulty: Difficulty;
  status: "awaiting_payment" | "ready" | "playing" | "completed" | "expired";
  currentPuzzle: number;
  totalPuzzles?: number;
  memberIds: string[];
  members: TeamMember[];
  paid: boolean;
  demo?: boolean;
  hintCount?: number;
  usedHintKeys?: string[];
  joinCode?: string;
  ticketId?: string;
  accessStartsOn?: string;
  accessEndsOn?: string;
  createdAt?: Timestamp;
}

export interface Puzzle {
  id: string;
  order: number;
  title: string;
  story: string;
  question: string;
  answer: string;
  hints: string[];
  latitude?: number;
  longitude?: number;
  radiusMeters?: number;
}

export interface SiteAnnouncement {
  text: string;
  active: boolean;
  updatedAt?: Timestamp;
}

export interface AdminMessage {
  id: string;
  teamId: string;
  teamName: string;
  gameTitle: string;
  leaderId: string;
  leaderEmail: string;
  message: string;
  status: "new" | "resolved";
  createdAt?: Timestamp;
  resolvedAt?: Timestamp | null;
}

export interface BusinessInquiry {
  id: string;
  companyName: string;
  contactName: string;
  email: string;
  phone?: string;
  inquiryType: "team_building" | "promotion" | "both";
  teamSize?: number | null;
  details: string;
  status: "new" | "contacted" | "closed";
  createdAt?: Timestamp;
  updatedAt?: Timestamp | null;
}

export const difficultyLabels: Record<Difficulty, string> = {
  story: "Mode Histoire · facile",
  investigation: "Mode Enquête · moyen",
  expert: "Mode Enquête · difficile",
};
